// Unpublished Work © 2025-2026 Deere & Company.
import {replaceTranslationNames} from 'Utils/translation-utils';
import ValidationInput from 'Ui/components/common/form/validation-input';
import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {getFormattedCurrency, getCurrencySymbol} from 'Utils/unit-conversion-utils';
import {IconRemove} from '@deere/icons';
import {isNullOrUndefined} from 'Common/utils/validation-utils';
import ReadOnlyWrapperComponent from 'Ui/components/common/form/read-only/read-only-wrapper-component';
import {clsx} from 'clsx';
import {IconButton, MultiSelect} from '@deere/isg.component-library';
import {openDialog as openReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {connect} from 'react-redux';

const ICON_REMOVE_STYLE = {
    style: {
        height: '28px',
        width: '28px'
    }
};

function handleBinLocationChange(binLocations, binLocationId, onChange, part) {
    const binLocation = binLocations.filter((bL) => bL.id === binLocationId)[0];

    if (!binLocation) {
        return;
    }
    onChange('binLocation', binLocation.title, part);
    onChange('binLocationId', binLocationId, part);
}

function SelectedPartQuantityWithCost({
    binLocations = [],
    currencyPreference,
    openBinLocationDialog,
    inventoryId,
    fetchBinLocations,
    isReceiving,
    onChange,
    onClear,
    part,
    quantityRequired = true,
    setValid,
    translations,
    disabled,
    readOnly,
    hideBinLocationInput,
    hideFinancialData
}) {
    const quantityLabel = React.useMemo(() => {
        if (isReceiving) {
            return translations.ONLINK_RECEIVED_QUANTITY;
        } else if (!isNullOrUndefined(part.quantityReceived)) {
            return translations.ONLINK_ORDERED_QUANTITY;
        }

        return translations['Observation.QUANTITY'];
    }, [part]);

    function onNewBinLocation(binLocationId, updatedBinLocations) {
        handleBinLocationChange(updatedBinLocations, binLocationId, onChange, part);
    }

    const addBinLocationFooterAction = {
        disabled: false,
        onClick() {
            openBinLocationDialog({
                inventoryId,
                inventoryBinLocations: binLocations,
                translations,
                fetchBinLocations,
                onNewBinLocation
            });
        },
        title: replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
            '0': translations.ONLINK_BIN_LOCATION
        })
    };

    const currencySymbol = getCurrencySymbol({
        currencyPreference
    });

    return (
        <div
            className={clsx('parts-selected-row-container', {
                'no-border': hideBinLocationInput
            })}
        >
            <div
                className='parts-selected-row'
                key={part.id}
            >
                <div className='part-label'>
                    <div className='title'>
                        {part.title}
                    </div>
                    <div className='sub-title'>
                        {part.subtitle}
                    </div>
                </div>
                <ReadOnlyWrapperComponent
                    label={quantityLabel}
                    readOnly={readOnly}
                    readOnlyProps={{
                        readOnlyValueClassName: 'parts-orders-cost'
                    }}
                    value={part.quantity}
                    wrappedComponent={ValidationInput}
                    wrappedProps={{
                        disabled,
                        className: 'full-w-mobile',
                        errors: {
                            rangeUnderflow: replaceTranslationNames(translations.VALUE_GREATER_THAN_OR_EQUAL, {
                                '0': 0
                            }),
                            rangeOverflow: `${translations.ONLINK_VALUE_LESS_THAN_OR_EQUAL} ${part.quantityMax}`
                        },
                        max: part.quantityMax,
                        min: 0,
                        name: `${part.id}quantity`,
                        'data-testid': `part-${part.id}-quantity-input`,
                        onChange: (event) => onChange('quantity', event.target.value, part),
                        placeholder: part.quantityMax,
                        required: quantityRequired,
                        setValid,
                        step: 'any',
                        tabIndex: 0,
                        type: 'number'
                    }}
                />
                {
                    !hideFinancialData &&
                    <ReadOnlyWrapperComponent
                        label={`${translations.ONLINK_UNIT_COST} (${currencySymbol})`}
                        readOnly={readOnly}
                        readOnlyProps={{
                            readOnlyValueClassName: 'parts-orders-cost'
                        }}
                        value={part.unitCost}
                        wrappedComponent={ValidationInput}
                        wrappedProps={{
                            disabled,
                            min: 0,
                            name: `${part.id}unitCost`,
                            'data-testid': `part-${part.id}-unit-cost-input`,
                            onChange: (event) => onChange('unitCost', event.target.value, part),
                            required: true,
                            setValid,
                            step: 'any',
                            tabIndex: 0,
                            type: 'number'
                        }}
                    />
                }
                {
                    !hideFinancialData && <div className='quantity-with-cost-end-column'>
                        <div className='part-selected-cost-column'>
                            <div
                                className={clsx('parts-selected-cost-label', {
                                    'readonly-cost-label': readOnly
                                })}
                            >{`${translations.ONLINK_TOTAL_COST} (${currencySymbol})`}</div>
                            {getFormattedCurrency(part.quantity * part.unitCost, {
                                currencyPreference
                            })}
                        </div>
                        {
                            !disabled && !isNullOrUndefined(onClear) && !readOnly &&
                            <IconButton
                                onClick={() => onClear(part)}
                                variant='borderless'
                            >
                                <IconRemove {...ICON_REMOVE_STYLE}/>
                            </IconButton>
                        }
                    </div>
                }
            </div>
            {!hideBinLocationInput && <div className='settings-group mobile'>
                <MultiSelect
                    footerAction={addBinLocationFooterAction}
                    items={binLocations}
                    label={translations.ONLINK_BIN_LOCATION}
                    labels={{
                        placeholder: replaceTranslationNames(translations.SELECT_PLACEHOLDER, {
                            '0': translations.ONLINK_BIN_LOCATION
                        }),
                        emptyList: translations.ONLINK_NO_BIN_LOCATIONS_FOUND
                    }}
                    onChange={(binLocationIds) => {
                        handleBinLocationChange(binLocations, binLocationIds[0], onChange, part);
                    }}
                    selectedIds={[part.binLocationId]}
                    single
                />
            </div>}

        </div>
    );
}

SelectedPartQuantityWithCost.propTypes = {
    binLocations: PropTypes.arrayOf(PropTypes.object),
    currencyPreference: PropTypes.string,
    disabled: PropTypes.bool,
    fetchBinLocations: PropTypes.func,
    hideBinLocationInput: PropTypes.bool,
    hideFinancialData: PropTypes.bool,
    inventoryId: PropTypes.string,
    isMobile: PropTypes.bool,
    isReceiving: PropTypes.bool,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    openBinLocationDialog: PropTypes.func,
    part: PropTypes.object,
    quantityRequired: PropTypes.bool,
    readOnly: PropTypes.bool,
    setValid: PropTypes.func,
    translations: PropTypes.translations
};

export function mapDispatchToProps(dispatch) {
    return {
        openBinLocationDialog(value) {
            dispatch(openReduxDialog(dialogTypes.BIN_LOCATION_DIALOG, value));
        }
    };
}

export default connect(null, mapDispatchToProps)(SelectedPartQuantityWithCost);
